import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { PaymentStatusEnum } from '../../enums/payment-status.enum';
import { TransactionTypeEnumV2 } from '../../enums/wallet.enum';
import { IUser } from '../../interfaces/users/user.interface';
import { ICredit, IDebit, IUnload, IWalletV2 } from '../../interfaces/wallet.interface';
import { WalletService } from '../../services/wallet.service';
import { CreditWallet, DebitWallet, GetWallets, ResetWallet, TransferMoney, UnloadWallet } from '../actions/wallet.action';
import { ITransactionV2 } from './../../interfaces/wallet.interface';
import { GetParentWallet } from './../actions/wallet.action';
import { AuthState } from './auth.state';
import { isNumeric } from 'rxjs/internal/util/isNumeric';

export interface WalletStateModel {
    wallets: IWalletV2[];
    currentWallet: IWalletV2;
    currentWalletParentId: number;
}

@State<WalletStateModel>({
    name: 'wallets',
    defaults: {
        wallets: [],
        currentWallet: null,
        currentWalletParentId: null,
    },
})
@Injectable()
export class WalletState {
    constructor(private walletService: WalletService, private store: Store) {}

    @Selector()
    public static wallets(state: WalletStateModel): IWalletV2[] {
        return state.wallets;
    }

    @Selector()
    public static currentWallet(state: WalletStateModel): IWalletV2 {
        return state.currentWallet;
    }

    @Selector()
    public static toPay(state: WalletStateModel): number {
        return state.currentWallet?.to_pay || 0;
    }

    @Selector()
    public static amountAvailable(state: WalletStateModel): number {
        return state.currentWallet?.credit || 0;
    }

    @Selector()
    public static transactionsToPay(state: WalletStateModel): ITransactionV2[] {
        return (
            state.currentWallet?.transactions?.filter(
                (t: ITransactionV2) => t.status === PaymentStatusEnum.CREATED && t.type === TransactionTypeEnumV2.DEBIT
            ) || []
        );
    }

    @Selector()
    public static transactionHistory(state: WalletStateModel): ITransactionV2[] {
        return state.currentWallet?.transactions || [];
    }

    @Selector()
    public static isPositiveBalance(state: WalletStateModel): boolean {
        return state.currentWallet?.credit > state.currentWallet?.to_pay;
    }

    @Selector()
    public static currentWalletParentId(state: WalletStateModel): number {
        return state.currentWalletParentId;
    }

    @Action(GetWallets)
    public getWallets(ctx: StateContext<WalletStateModel>, action: GetWallets): Observable<IWalletV2[]> {
        return this.walletService.getMyWalletsV2(action?.studentId).pipe(
            tap((result: IWalletV2[]) => {
                const mutableParentWallet = this.getMutableParentWallet(result, action?.studentId, null);
                if (result) {
                    ctx.patchState({
                        wallets: result,
                        currentWallet: mutableParentWallet,
                        currentWalletParentId: null,
                    });
                }
            })
        );
    }

    @Action(GetParentWallet)
    public getParentWallet(ctx: StateContext<WalletStateModel>, action: GetParentWallet): Observable<IWalletV2[]> {
        const studentId = isNumeric(action?.student) ? action.student : action.student?.id;

        const state: WalletStateModel = ctx.getState();

        const mutableParentWallet = this.getMutableParentWallet(state.wallets, action?.student, action.parentId);

        if (mutableParentWallet && !action.forceRequest) {
            ctx.patchState({
                currentWallet: mutableParentWallet,
                currentWalletParentId: action.parentId,
            });
            return of([mutableParentWallet]);
        } else if ((!mutableParentWallet || action.forceRequest) && studentId) {
            return this.walletService.getMyWalletsV2(studentId).pipe(
                tap((result: IWalletV2[]) => {
                    if (result) {

                        const mutableParentWallet = this.getMutableParentWallet(result, action?.student, action.parentId);
                        ctx.patchState({
                            wallets: result,
                            currentWallet: mutableParentWallet,
                            currentWalletParentId: action.parentId,
                        });
                    }
                })
            );
        } else {
            return of(null);
        }
    }

    @Action(CreditWallet)
    public creditWallet(ctx: StateContext<WalletStateModel>, action: CreditWallet): Observable<ICredit> {
        const state: WalletStateModel = ctx.getState();
        return this.walletService.creditWallet(action.credit).pipe(
            tap((result: ICredit) => {
                if (result) {
                    const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
                    const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                    const studentId: number = connectedUser.id !== currentUser.id ? currentUser.id : null;
                    if (state.currentWalletParentId) {
                        ctx.dispatch(new GetParentWallet(state.currentWalletParentId, studentId, true));
                    } else {
                        ctx.dispatch(new GetWallets(studentId));
                    }
                }
            })
        );
    }

    @Action(DebitWallet)
    public debitWallet(ctx: StateContext<WalletStateModel>, action: DebitWallet): Observable<IDebit> {
        const state: WalletStateModel = ctx.getState();
        return this.walletService.debitWallet(action.debit).pipe(
            tap((result: IDebit) => {
                if (result) {
                    const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
                    const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                    const studentId: number = connectedUser.id !== currentUser.id ? currentUser.id : null;
                    if (state.currentWalletParentId) {
                        ctx.dispatch(new GetParentWallet(state.currentWalletParentId, studentId, true));
                    } else {
                        ctx.dispatch(new GetWallets(studentId));
                    }
                }
            })
        );
    }

    @Action(UnloadWallet)
    public unloadWallet(ctx: StateContext<WalletStateModel>, action: UnloadWallet): Observable<IUnload> {
        const state: WalletStateModel = ctx.getState();
        return this.walletService.unloadWallet(action.unload, action.parentId).pipe(
            tap((result: IUnload) => {
                if (result) {
                    const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
                    const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                    const studentId: number = connectedUser.id !== currentUser.id ? currentUser.id : null;
                    if (state.currentWalletParentId) {
                        ctx.dispatch(new GetParentWallet(state.currentWalletParentId, studentId, true));
                    } else {
                        ctx.dispatch(new GetWallets(studentId));
                    }
                }
            })
        );
    }

    @Action(TransferMoney)
    public transferMoney(ctx: StateContext<WalletStateModel>, action: TransferMoney): void {
        const state: WalletStateModel = ctx.getState();
        return this.walletService.transferMoney(action.transfer, action.userToId).pipe(
            tap((result: any) => {
                // todo modify 'any' with response type from back
                if (result) {
                    const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
                    const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
                    const studentId: number = connectedUser.id !== currentUser.id ? currentUser.id : null;
                    if (state.currentWalletParentId) {
                        ctx.dispatch(new GetParentWallet(state.currentWalletParentId, studentId, true));
                    } else {
                        ctx.dispatch(new GetWallets(studentId));
                    }
                }
                // TODO modify dispatch(new GetMyWallets) ? [TRANSFER]
            })
        );
    }

    @Action(ResetWallet)
    public resetWallet(ctx: StateContext<WalletStateModel>): void {
        ctx.patchState({
            currentWallet: null,
            wallets: [],
        });
    }

    private getMutableParentWallet(result: IWalletV2[], student: IUser | number | undefined, parentId: number): IWalletV2 {
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);

        if (connectedUser.type === 'parent') {
            const parentWallet: IWalletV2 = result.find((w) => w.parents.find((p) => p.id === connectedUser.id));
            const othersWallets: IWalletV2[] = result.filter((w) => w.parents.find((p) => p.id !== connectedUser.id));

            const id = isNumeric(student) ? student : student?.id || null;
            const mutableParentWallet = {
                ...parentWallet,
                transactions: [...(parentWallet.transactions || [])]
            };

            if (id && currentUser?.id === id && currentUser?.type === 'student') {
                if (currentUser?.settings?.costs_repartition_id === 2) {
                    for (const otherWallet of othersWallets) {
                        mutableParentWallet.transactions.push(
                            ...(otherWallet.transactions || [])
                        );
                    }
                }
            }

            return mutableParentWallet;
        } else if (parentId) {
            return result.find((w) => w.parents.find((p) => p.id === parentId));
        }

        return result?.[0] || null;
    }
}
